import { z } from 'zod';
import { UuidSchema } from './common';
import { LateAnswerPolicySchema } from './enums';

/**
 * §13, §26 ТЗ и решения E1–E10 Дополнения №1.
 * Схемы значений бизнес-настроек общие: их читает форма админки и валидирует
 * сервер. Значения по умолчанию — политика сервера, они остаются в API.
 */

/** §9.4: правило негативной оценки. Снимок уходит в версию анкеты при публикации. */
export const NegativeRuleSchema = z.object({
  type: z.literal('threshold'),
  operator: z.enum(['lte', 'lt', 'eq']),
  value: z.number().int().min(1).max(5),
  /** any_required_scored — хватает одного низкого ответа среди оцениваемых. */
  scope: z.enum(['any_required_scored', 'all_required_scored', 'average']),
});
export type NegativeRule = z.infer<typeof NegativeRuleSchema>;

export const TaskPriorityMapSchema = z.object({
  INCIDENT: z.enum(['LOW', 'NORMAL', 'HIGH']),
  COMPLAINT: z.enum(['LOW', 'NORMAL', 'HIGH']),
  EXTRA_SERVICE: z.enum(['LOW', 'NORMAL', 'HIGH']),
  CHANGE_SERVICE: z.enum(['LOW', 'NORMAL', 'HIGH']),
});
export type TaskPriorityMap = z.infer<typeof TaskPriorityMapSchema>;

export const RetentionDaysSchema = z.object({
  audit: z.number().int().positive(),
  runs: z.number().int().positive(),
});

/**
 * Дополнение №1, А13. Наблюдатели во всех задачах Bitrix24 (руководитель
 * производственного отдела и его заместитель) — сотрудники сервиса.
 */
export const TaskObserverIdsSchema = z
  .array(UuidSchema)
  .max(10)
  .refine((ids) => new Set(ids).size === ids.length, { message: 'Сотрудники повторяются' });

export const SETTING_SCHEMAS = {
  negative_rule: NegativeRuleSchema,
  invite_ttl_hours: z.number().int().min(1).max(720),
  invite_max_attempts: z.number().int().min(1).max(20),
  require_phone: z.boolean(),
  require_email: z.boolean(),
  late_answer_policy: LateAnswerPolicySchema,
  notify_requester_on_close: z.boolean(),
  task_reaction_hours: z.number().int().min(1).max(720),
  task_priority_map: TaskPriorityMapSchema,
  task_observer_ids: TaskObserverIdsSchema,
  consent_text: z.string(),
  retention_days: RetentionDaysSchema,
} as const;

export const AppSettingsSchema = z.object(SETTING_SCHEMAS);
export type AppSettings = z.infer<typeof AppSettingsSchema>;

export const SettingKeySchema = z.enum(
  Object.keys(SETTING_SCHEMAS) as [
    keyof typeof SETTING_SCHEMAS,
    ...(keyof typeof SETTING_SCHEMAS)[],
  ],
);
export type SettingKeyName = z.infer<typeof SettingKeySchema>;

/** Значение валидируется сервером схемой конкретного ключа. */
export const UpdateSettingRequestSchema = z.object({ value: z.unknown() });
export type UpdateSettingRequest = z.infer<typeof UpdateSettingRequestSchema>;
